import { createContext, ReactNode, Reducer, useContext, useMemo, useReducer } from 'react';
import { FILTERS } from '../constants/filters';

export type FilterName = keyof typeof FILTERS;

export interface FilterOption {
  value: string;
  label: string;
}

type ActiveFiltersType = Record<FilterName, FilterOption>;

interface IPartnerOptions {
  options: FilterOption[];
}

type State = {
  filters: ActiveFiltersType;
  partnerOptions: IPartnerOptions;
  season: Partial<FilterOption>;
  count: number;
};

type Dispatch = (action: Action) => void;

enum ActionTypes {
  SET_FILTER,
  SET_PARTNER_OPTIONS,
  SET_SEASON,
  RESET_FILTERS
}

interface SeasonPayload {
  season: FilterOption;
}

interface FilterPayload {
  filterName: FilterName;
  filter: FilterOption;
}

type SetFilterAction = {
  type: ActionTypes.SET_FILTER;
  payload: FilterPayload;
};

type SetSeasonAction = {
  type: ActionTypes.SET_SEASON;
  payload: SeasonPayload;
};

type SetPartnerOptionsAction = {
  type: ActionTypes.SET_PARTNER_OPTIONS;
  payload: IPartnerOptions;
};

type ResetFiltersAction = {
  type: ActionTypes.RESET_FILTERS;
};

type Action = SetFilterAction | SetPartnerOptionsAction | SetSeasonAction | ResetFiltersAction;

interface IActiveFilters {
  state: State;
  dispatch: Dispatch;
}

const actions = {
  setFilter: (filterName: FilterName, filter: FilterOption): SetFilterAction => ({
    type: ActionTypes.SET_FILTER,
    payload: { filterName, filter }
  }),
  setPartnerOptions: (partners: IPartnerOptions): SetPartnerOptionsAction => ({
    type: ActionTypes.SET_PARTNER_OPTIONS,
    payload: partners
  }),
  setSeason: (season: FilterOption): SetSeasonAction => {
    return {
      type: ActionTypes.SET_SEASON,
      payload: { season }
    };
  },
  resetFilters: (): ResetFiltersAction => ({ type: ActionTypes.RESET_FILTERS })
};

const ActiveFilters = createContext<IActiveFilters | undefined>(undefined);

let FILTERS_DEFAULT: Partial<ActiveFiltersType> = {};

for (const key in FILTERS) {
  FILTERS_DEFAULT[key as FilterName] = FILTERS[key as FilterName].defaultOption;
}

const initialState: State = {
  season: { label: '', value: '' },
  filters: { ...FILTERS_DEFAULT } as ActiveFiltersType,
  partnerOptions: { options: [] },
  count: 0
};

const reducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_FILTER:
      const { filterName, filter } = action.payload;
      state.filters[filterName] = filter;
      let newCount = 0;
      for (let filter in state.filters) {
        if (state.filters[filter as FilterName].value) newCount++;
      }
      return { ...state, count: newCount };
    case ActionTypes.SET_PARTNER_OPTIONS:
      const { options } = action.payload;
      state.partnerOptions = { options };

      return { ...state };
    case ActionTypes.SET_SEASON:
      const { season } = action.payload;
      state.season = season;
      return { ...state, season };
    case ActionTypes.RESET_FILTERS:
      return {
        ...state,
        filters: {
          ...FILTERS_DEFAULT
        } as ActiveFiltersType,
        count: 0
      };
    default:
      return state;
  }
};

const Provider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value: IActiveFilters = useMemo(() => {
    return { state, dispatch };
  }, [state]);
  return <ActiveFilters.Provider value={value}>{children}</ActiveFilters.Provider>;
};

const useActiveFilters = () => {
  const context = useContext(ActiveFilters);
  if (context === undefined) {
    throw new Error('useActiveFilters must be used within a stateProvider');
  }
  return context;
};

export { Provider as ActiveFiltersProvider, useActiveFilters, actions as activeFiltersActions };
